// Client-side mirror of ember/src/utils/bookingValidation.ts#getSlotStartDate (R24).
// A slot must start at least one calendar month from the request date, on the first
// weekday-matching date of its month. Server remains the source of truth; this exists so the
// "Confirm your slot" modal can show the same start date the server will persist.
//
// UTC-anchored calendar arithmetic (Date.UTC / getUTC*), matching BookingOccurrenceList's
// enumerateOccurrences and Ember's UTC computation. `dayOfWeek` must be a UTC weekday
// (0=Sunday…6=Saturday, e.g. `bookingDate.getUTCDay()`) so the derived date lands on the same
// UTC calendar day the rest of the calendar treats as the booking's day — deriving it in the
// browser's local time drifted a day west of UTC (the travelling-member bug).

const pad2 = (n: number): string => String(n).padStart(2, "0");

export function getSlotStartDate(bookingDate: Date, dayOfWeek: number): Date {
  // minStart = bookingDate + 1 calendar month.
  const minStart = new Date(
    Date.UTC(bookingDate.getUTCFullYear(), bookingDate.getUTCMonth() + 1, bookingDate.getUTCDate()),
  );

  // Walk forward month-by-month until the month's first matching weekday is >= minStart.
  // Converges within two iterations in practice; 12 is a defensive ceiling.
  for (let monthOffset = 0; monthOffset < 12; monthOffset++) {
    const monthStart = new Date(Date.UTC(minStart.getUTCFullYear(), minStart.getUTCMonth() + monthOffset, 1));
    const offset = (dayOfWeek - monthStart.getUTCDay() + 7) % 7;
    const candidate = new Date(Date.UTC(monthStart.getUTCFullYear(), monthStart.getUTCMonth(), 1 + offset));
    if (candidate.getTime() >= minStart.getTime()) {
      return candidate;
    }
  }

  throw new Error("Failed to derive slot start date");
}

export function getSlotStartIso(bookingDate: Date, dayOfWeek: number): string {
  const d = getSlotStartDate(bookingDate, dayOfWeek);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}
